import { Button, Stack, Typography } from '@mui/material';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * A single top-level error boundary wrapping the app (docs/development/FrontendGuidelines.md §9.1). Renders a
 * full-page fallback with a reload action, never a blank white screen. Reports to Application
 * Insights once `config/env.ts`'s `appInsightsConnectionString` is wired to a real telemetry SDK
 * Logged to the console for now so the failure is never silently swallowed.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Unhandled render error', error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ minHeight: '100vh', textAlign: 'center', p: 3 }}
        >
          <Typography variant="h5" fontWeight={700}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            We hit an unexpected error. Reloading the page usually fixes this. If it keeps
            happening, contact support.
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Reload
          </Button>
        </Stack>
      );
    }

    return this.props.children;
  }
}
